import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { referralsAPI, type ReferralLink } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'

/**
 * On a creator's page, for someone subscribed to them: a lasting share link,
 * how many subscribers it brought in and, when the creator gives them, the
 * free months it earned (each once the new subscriber has paid).
 */
const InviteFriends = ({ creatorId, creatorName }: { creatorId: number; creatorName: string }) => {
  const [link, setLink] = useState<ReferralLink | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let live = true
    referralsAPI
      .mine(creatorId)
      .then((mine) => live && setLink(mine))
      .catch(() => live && setLink(null))
    return () => {
      live = false
    }
  }, [creatorId])

  const url = link ? `${window.location.origin}${link.share_path}` : null

  const getLink = async () => {
    setBusy(true)
    try {
      setLink(await referralsAPI.link(creatorId))
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't make your link"))
    } finally {
      setBusy(false)
    }
  }

  const copy = async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const brought = link?.conversions.length ?? 0
  const waiting = link?.conversions.filter((c) => c.reward_status === 'waiting_payment').length ?? 0

  return (
    <section aria-labelledby="invite-heading" className="rounded-lg border border-gray-200 bg-white p-4" data-testid="invite-friends">
      <h2 id="invite-heading" className="text-lg font-semibold text-gray-900">
        Invite friends to {creatorName}
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        {link && !link.rewards_enabled
          ? 'Share your link with anyone who’d like these calls.'
          : `For each friend who subscribes through your link, your next month with ${creatorName} is free, once they’ve paid for theirs. Up to 12 months a year.`}
      </p>
      {url ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <code className="min-w-0 break-all rounded bg-gray-50 px-2 py-1 text-sm text-gray-900" data-testid="invite-url">
            {url}
          </code>
          <button
            type="button"
            onClick={copy}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={getLink}
          disabled={busy}
          className="mt-3 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy ? 'Making your link…' : 'Get your invite link'}
        </button>
      )}
      {link && (
        <p className="mt-2 text-xs text-gray-500" data-testid="invite-stats">
          {brought} {brought === 1 ? 'friend' : 'friends'} subscribed through your link
          {link.rewards_enabled || link.months_earned > 0
            ? ` · ${link.months_earned} free ${link.months_earned === 1 ? 'month' : 'months'} earned`
            : ''}
          {waiting > 0 ? ` · ${waiting} waiting for their first payment` : ''}
        </p>
      )}
    </section>
  )
}

export default InviteFriends
